/**
 * Point cloud utilities for converting geometries to particle fields.
 *
 * Provides functions to sample points along edges and surfaces of
 * Three.js geometries, producing Float32Arrays for use with THREE.Points.
 */
/**
 * Sample N points uniformly along the edges of a BufferGeometry.
 * Works with EdgesGeometry or any geometry with position attribute pairs.
 *
 * @param {THREE.BufferGeometry} edgesGeo - EdgesGeometry or line segment geometry
 * @param {number} count - Number of points to sample
 * @param {number} [jitter=0.05] - Random displacement magnitude
 * @returns {Float32Array} positions array (count * 3)
 */
export function sampleEdgePoints(edgesGeo, count, jitter = 0.05) {
    const posAttr = edgesGeo.getAttribute('position');
    const positions = new Float32Array(count * 3);
    const segmentCount = posAttr.count / 2;

    for (let i = 0; i < count; i++) {
        // Pick a random segment
        const seg = Math.floor(Math.random() * segmentCount);
        const idx = seg * 2;

        // Interpolate along segment
        const t = Math.random();
        const x = posAttr.getX(idx) + (posAttr.getX(idx + 1) - posAttr.getX(idx)) * t;
        const y = posAttr.getY(idx) + (posAttr.getY(idx + 1) - posAttr.getY(idx)) * t;
        const z = posAttr.getZ(idx) + (posAttr.getZ(idx + 1) - posAttr.getZ(idx)) * t;

        positions[i * 3] = x + (Math.random() - 0.5) * jitter;
        positions[i * 3 + 1] = y + (Math.random() - 0.5) * jitter;
        positions[i * 3 + 2] = z + (Math.random() - 0.5) * jitter;
    }

    return positions;
}

/**
 * Sample N points on the surface of a box.
 *
 * @param {number} w - Width (X)
 * @param {number} h - Height (Y)
 * @param {number} d - Depth (Z)
 * @param {number} count - Number of points
 * @param {number} [jitter=0.08] - Random displacement
 * @returns {Float32Array} positions array (count * 3)
 */
export function sampleBoxSurface(w, h, d, count, jitter = 0.08) {
    const positions = new Float32Array(count * 3);
    const hw = w / 2, hh = h / 2, hd = d / 2;

    // Face areas for weighted sampling
    const areas = [w * h, w * h, w * d, w * d, h * d, h * d];
    const total = areas.reduce((a, b) => a + b, 0);
    const cumulative = [];
    let sum = 0;
    for (const a of areas) {
        sum += a / total;
        cumulative.push(sum);
    }

    for (let i = 0; i < count; i++) {
        const r = Math.random();
        let face = 0;
        for (let f = 0; f < cumulative.length; f++) {
            if (r <= cumulative[f]) { face = f; break; }
        }

        let x, y, z;
        const u = Math.random() - 0.5;
        const v = Math.random() - 0.5;

        switch (face) {
            case 0: x = u * w; y = v * h; z = hd; break;    // front
            case 1: x = u * w; y = v * h; z = -hd; break;   // back
            case 2: x = u * w; y = hh; z = v * d; break;    // top
            case 3: x = u * w; y = -hh; z = v * d; break;   // bottom
            case 4: x = hw; y = u * h; z = v * d; break;    // right
            default: x = -hw; y = u * h; z = v * d; break;  // left
        }

        positions[i * 3] = x + (Math.random() - 0.5) * jitter;
        positions[i * 3 + 1] = y + (Math.random() - 0.5) * jitter;
        positions[i * 3 + 2] = z + (Math.random() - 0.5) * jitter;
    }

    return positions;
}

/**
 * Sample points along line segment pairs from a raw position array.
 *
 * @param {Float32Array|number[]} linePositions - pairs of xyz coords
 * @param {number} count - Number of points
 * @param {number} [jitter=0.05]
 * @returns {Float32Array}
 */
export function sampleLineSegments(linePositions, count, jitter = 0.05) {
    const positions = new Float32Array(count * 3);
    const segmentCount = linePositions.length / 6;

    for (let i = 0; i < count; i++) {
        const seg = Math.floor(Math.random() * segmentCount);
        const base = seg * 6;
        const t = Math.random();

        positions[i * 3] = linePositions[base] + (linePositions[base + 3] - linePositions[base]) * t + (Math.random() - 0.5) * jitter;
        positions[i * 3 + 1] = linePositions[base + 1] + (linePositions[base + 4] - linePositions[base + 1]) * t + (Math.random() - 0.5) * jitter;
        positions[i * 3 + 2] = linePositions[base + 2] + (linePositions[base + 5] - linePositions[base + 2]) * t + (Math.random() - 0.5) * jitter;
    }

    return positions;
}

/**
 * Sample points in a grid pattern on the XZ plane.
 *
 * @param {number} size - Grid extent in each direction
 * @param {number} spacing - Distance between points
 * @param {number} [jitter=0.3] - Random displacement
 * @returns {Float32Array}
 */
export function sampleGridPoints(size, spacing, jitter = 0.3) {
    const points = [];
    const half = size / 2;

    for (let x = -half; x <= half; x += spacing) {
        for (let z = -half; z <= half; z += spacing) {
            // Distance-based probability (fade at edges)
            const dist = Math.sqrt(x * x + z * z);
            if (dist > half) continue;
            const prob = 1 - (dist / half) * 0.8;
            if (Math.random() > prob) continue;

            points.push(
                x + (Math.random() - 0.5) * jitter,
                (Math.random() - 0.5) * 0.02,
                z + (Math.random() - 0.5) * jitter
            );
        }
    }

    return new Float32Array(points);
}
/**
 * Features: breathing animation, size attenuation, bloom-compatible emissive.
 *
 * @param {string} color - Base color hex
 * @param {number} [size=0.15] - Point size
 * @param {number} [opacity=0.7] - Base opacity
 * @returns {THREE.ShaderMaterial}
 */
export function createPointCloudMaterial(color, size = 0.15, opacity = 0.7) {
    return new THREE.ShaderMaterial({
        transparent: true,
        depthWrite: false,
        blending: THREE.AdditiveBlending,
        uniforms: {
            uColor: { value: new THREE.Color(color) },
            uSize: { value: size },
            uOpacity: { value: opacity },
            uTime: { value: 0 },
            uIntensity: { value: 1.0 },
        },
        vertexShader: /* glsl */ `
            uniform float uSize;
            uniform float uTime;
            uniform float uIntensity;

            varying float vAlpha;
            varying float vDist;

            void main() {
                vec3 pos = position;

                // Subtle breathing displacement
                float phase = dot(position, vec3(1.0, 0.7, 0.5)) * 3.0 + uTime * 0.8;
                pos += normal * sin(phase) * 0.03 * uIntensity;

                // Small drift
                pos.y += sin(uTime * 0.5 + position.x * 2.0) * 0.02;

                vec4 mvPosition = modelViewMatrix * vec4(pos, 1.0);

                // Size attenuation
                gl_PointSize = uSize * (200.0 / -mvPosition.z);
                gl_PointSize = max(gl_PointSize, 1.0);
                gl_PointSize = min(gl_PointSize, 8.0);

                gl_Position = projectionMatrix * mvPosition;

                // Fade with distance
                vDist = -mvPosition.z;
                vAlpha = 1.0 - smoothstep(50.0, 150.0, vDist);
            }
        `,
        fragmentShader: /* glsl */ `
            uniform vec3 uColor;
            uniform float uOpacity;
            uniform float uIntensity;

            varying float vAlpha;
            varying float vDist;

            void main() {
                // Soft circular point
                float dist = length(gl_PointCoord - vec2(0.5));
                if (dist > 0.5) discard;

                float softEdge = 1.0 - smoothstep(0.2, 0.5, dist);
                float alpha = softEdge * uOpacity * vAlpha * uIntensity;

                // Slight bloom-friendly brightness boost at center
                vec3 color = uColor * (1.0 + (1.0 - dist * 2.0) * 0.3);

                gl_FragColor = vec4(color, alpha);
            }
        `,
    });
}
